import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import algosdk from "algosdk";

import useTheme from "hooks/useTheme";
import useNotification from "hooks/useNotification";

import { convertBase64, generateRandomIndexesOfArray, shuffleArray } from "helpers/functions";

import { addNewAccountInUserProfile, addNewAccountSecretKeyForUser } from "actions/accounts";

import CopyIcon from "theme/icons/Copy";
import SaveTextIcon from "theme/icons/SaveText";
import MailIcon from "theme/icons/Mail";
import CheckOkIcon from "theme/icons/CheckOK";
import UserIconRoundIcon from "theme/icons/UserIconRound";
import EditIcon from "theme/icons/Edit";

const GenerateAccount = () => {
	let account = algosdk.generateAccount();
	return algosdk.secretKeyToMnemonic(account.sk);
};

const CreateTAccount = ({ navigatePage }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();
	const hiddenImageFileInput = useRef();

	const updatingData = useSelector((state) => state.UpdatingData);

	const [currentPage, setCurrentPage] = useState(null);

	const [accountPhrase, setAccountPhrase] = useState(GenerateAccount());
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
	const [invalidPhraseInputWarningCount, setInvalidPhraseInputWarningCount] = useState(null);
	const [phraseList, setPhraseList] = useState(null);
	const [wordNumbers, setWordNumbers] = useState(null);
	const [testPhrases, setTestPhrases] = useState(null);
	const [accountName, setAccountName] = useState(null);
	const [accountImage, setAccountImage] = useState(null);

	useEffect(() => {
		if (accountPhrase) {
			setPhraseList(accountPhrase.split(" "));
			setCurrentPage(null);
			setCurrentQuestionIndex(1);
			setAccountName(null);
		}
	}, [accountPhrase]);

	useEffect(() => {
		if (phraseList) setWordNumbers(generateRandomIndexesOfArray(phraseList, 3));
	}, [phraseList]);

	useEffect(() => {
		if (wordNumbers) {
			let testQuestions = [];
			let tempShuffleArray = [];
			let wordNumber = null;

			let randomIndex = null;

			for (let i = 0; i < wordNumbers.length; i++) {
				tempShuffleArray = [];
				wordNumber = wordNumbers[i];

				//  set question phrases
				tempShuffleArray.push(phraseList[wordNumber]);

				for (let i = 0; i < 2; ) {
					randomIndex = Math.floor(Math.random() * (phraseList.length - 1));

					if (!tempShuffleArray.includes(phraseList[randomIndex])) {
						tempShuffleArray.push(phraseList[randomIndex]);
						i++;
					}
				}

				shuffleArray(tempShuffleArray);

				testQuestions.push({
					wordNumber: wordNumber,
					testPhrases: tempShuffleArray,
				});
			}
			setTestPhrases(testQuestions);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wordNumbers]);

	const handleFileRead = async (e) => {
		const file = e.target.files[0];
		const base64 = await convertBase64(file);

		setAccountImage(base64);
	};

	const SubmitPhrase = (phrase) => {
		if (phraseList[testPhrases[currentQuestionIndex - 1].wordNumber] === phrase) {
			setInvalidPhraseInputWarningCount(null);
			setCurrentQuestionIndex(currentQuestionIndex + 1);

			if (currentQuestionIndex === 3) setCurrentPage(3);
		} else if (invalidPhraseInputWarningCount === 1) {
			setInvalidPhraseInputWarningCount(null);
			setCurrentQuestionIndex(1);
			setWordNumbers(generateRandomIndexesOfArray(phraseList, 3));
		} else {
			setInvalidPhraseInputWarningCount(invalidPhraseInputWarningCount + 1);
		}
	};

	const AddAccount = async () => {
		var recoveredAccount = algosdk.mnemonicToSecretKey(accountPhrase);

		if (recoveredAccount.addr && recoveredAccount.sk) {
			dispatch(
				addNewAccountSecretKeyForUser(
					{ "account-address": recoveredAccount.addr, "secret-key": recoveredAccount.sk },
					notify
				)
			);

			dispatch(
				addNewAccountInUserProfile(
					{ "account-address": recoveredAccount.addr, name: accountName, "profile-image": accountImage },
					navigatePage,
					notify
				)
			);
		}

		recoveredAccount = null;
	};

	// const RegenerateAccount = () => {
	// 	setAccountPhrase(GenerateAccount());
	// };

	return (
		<div className="w-full space-x-4 items-center justify-center">
			<Transition
				as={Fragment}
				appear={true}
				show={true}
				enter="transition-opacity duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="transition-opacity duration-150"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<div className="items-center justify-center flex flex-col col-span-2 py-2 px-3 w-full space-y-4">
					<div className="py-1 px-2 items-center flex justify-start w-full">
						<div
							className={`flex items-center justify-center w-full text-2xl xl:text-3xl self-center font-bold ${themeColor?.text}`}
						>
							<h1 className="">{appLanguage?.createNewAccount}</h1>
						</div>
					</div>

					{!currentPage ? (
						<div className="flex flex-col w-full items-center justify-center space-y-7">
							<div
								className={`w-[60%] flex items-center justify-center space-x-6 border-b-2 pb-3 ${themeColor?.border}`}
							>
								<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.secretWordsEncryption}</p>
							</div>
							<div className="flex flex-col w-[60%] items-start justify-center space-y-3">
								<p className={`text-md font-bold ${themeColor?.text}`}>{appLanguage?.toSecureYourNewAccount}</p>
								<p className={`text-md text-justify ${themeColor?.text}`}>{appLanguage?.thoseSecretWordsWill}</p>
							</div>
							<div className="flex flex-col w-[60%] items-start justify-center space-y-3">
								<p className={`text-md font-bold ${themeColor?.text}`}>{appLanguage?.makeSureYouSave}</p>
								<p className={`text-md text-justify ${themeColor?.text}`}>{appLanguage?.anyoneWithYourSecret}</p>
							</div>
							<button
								className={`text-white ${themeColor?.bg} py-3 px-8 mt-5 rounded-md hover:shadow-lg smooth`}
								onClick={() => {
									setCurrentPage(1);
								}}
							>
								{appLanguage?.continue}
							</button>
						</div>
					) : (
						<>
							<ol className="w-full items-center flex">
								<li className="w-[50%] relative">
									<div className="flex items-center">
										<div
											className={`flex z-10 justify-center items-center px-2 py-1 rounded-full shrink-0 ${
												currentPage === 1
													? `text-white animate-pulse ${themeColor?.bg}`
													: `bg-white border ${themeColor?.text + " " + themeColor?.border}`
											}`}
										>
											<p className="text-sm">01</p>
										</div>
										<div className={`hidden sm:flex w-full h-0.5 ${themeColor?.bg}`}></div>
									</div>
								</li>
								<li className="w-min relative">
									<div className="flex items-center justify-center">
										<div
											className={`flex z-10 justify-center items-center px-2 py-1 rounded-full shrink-0 ${
												currentPage === 2
													? `text-white animate-pulse ${themeColor?.bg}`
													: `bg-white border ${themeColor?.text + " " + themeColor?.border}`
											}`}
										>
											<p className="text-sm">02</p>
										</div>
									</div>
								</li>
								<li className="w-[50%] relative">
									<div className="flex items-center">
										<div className={`hidden sm:flex w-full h-0.5 ${themeColor?.bg}`}></div>
										<div
											className={`flex z-10 justify-center items-center px-2 py-1 rounded-full shrink-0 ${
												currentPage === 3
													? `text-white animate-pulse ${themeColor?.bg}`
													: `bg-white border ${themeColor?.text + " " + themeColor?.border}`
											}`}
										>
											<p className="text-sm">03</p>
										</div>
									</div>
								</li>
							</ol>
							<ol className="w-full items-center justify-between flex">
								<li className="w-[50%]">
									<p className={`block mb-2 text-xs font-medium leading-none ${themeColor?.text}`}>
										{appLanguage?.mySecretWords}
									</p>
								</li>
								<li className="w-min">
									<p className={`w-max block mb-2 text-xs font-medium leading-none ${themeColor?.text}`}>
										{appLanguage?.verifyYourSecretWords}
									</p>
								</li>
								<li className="w-[50%]">
									<p className={`text-right block mb-2 text-xs font-medium leading-none ${themeColor?.text}`}>
										{appLanguage?.completeAccount}
									</p>
								</li>
							</ol>

							{/* Secret Words Section */}
							{currentPage === 1 ? (
								<div className="w-full flex-col space-y-5">
									<div className="flex-col w-full space-y-4 py-5">
										<div className="flex w-full items-center justify-center space-x-2">
											<p className={`text-sm font-medium ${themeColor?.text}`}>
												{appLanguage?.yourSecretWordsWillAllowYou}
											</p>
										</div>

										<div className="flex flex-col w-full items-start justify-start">
											<div
												className={`w-full flex flex-wrap justify-between ${themeColor?.bg} bg-opacity-10 px-7 py-3 rounded-lg`}
											>
												{phraseList &&
													phraseList.map((phrase, index) => (
														<div className="flex w-[20%] items-start justify-start py-2" key={index}>
															<p className={`pr-2 text-md ${themeColor?.text}`}>{index + 1}.</p>
															<p className={`pr-2 text-md ${themeColor?.text}`}>{phrase}</p>
														</div>
													))}
											</div>
										</div>
									</div>

									<div className="flex items-center justify-center w-full space-x-4">
										<CopyIcon
											className="w-6 cursor-pointer"
											fill={themeColor?.hex}
											onClick={() => {
												navigator.clipboard.writeText(accountPhrase);
												notify("success", "Copied to clipboard");
											}}
										/>
										<SaveTextIcon className="w-6" fill={themeColor?.hex} />
										<MailIcon className="w-6" fill={themeColor?.hex} />
									</div>

									<div className="flex flex-col w-full items-center justify-center space-x-2">
										<button
											className={`text-white ${themeColor?.bg} py-3 px-8 mt-5 rounded-md hover:shadow-lg smooth`}
											onClick={() => {
												setCurrentPage(2);
												setWordNumbers(generateRandomIndexesOfArray(phraseList, 3));
											}}
										>
											{appLanguage?.continue}
										</button>
									</div>
								</div>
							) : // Verify Secret Words Section
							currentPage === 2 ? (
								<div className="w-full flex-col space-y-9">
									<div className="flex flex-col items-start justify-start w-full">
										<p className={`text-md ${themeColor?.text}`}>
											{appLanguage?.toEnsureYouHaveProperlyCopied}
										</p>
										{invalidPhraseInputWarningCount && (
											<p className={`text-md text-candy-red`}>{appLanguage?.invalidPhraseEntered}</p>
										)}
									</div>

									{testPhrases && testPhrases.length && currentQuestionIndex && (
										<div className="flex flex-col w-full items-center justify-center space-y-9">
											{testPhrases &&
												testPhrases.map((testPhrase, wordIndex) => (
													<div className={`w-full flex-col space-y-3`} key={wordIndex}>
														<div className="flex w-full items-center justify-start space-x-4">
															<p className={`font-bold ${themeColor?.text}`}>
																{appLanguage?.mySecretWord +
																	" " +
																	(testPhrase?.wordNumber + 1) +
																	" " +
																	appLanguage?.is}
															</p>

															{wordIndex < currentQuestionIndex - 1 && (
																<CheckOkIcon className={`w-4`} fill={themeColor?.hex} />
															)}
														</div>

														<div className="flex w-full items-center justify-center space-x-9">
															{testPhrases[wordIndex] &&
																testPhrases[wordIndex].testPhrases.map((phrase, index) => (
																	<div className="w-[20%] flex-col" key={index}>
																		<button
																			className={`font-bold w-full text-white ${themeColor?.bg} py-3 rounded-md hover:shadow-lg disabled:bg-opacity-10 disabled:shadow-inner smooth`}
																			onClick={() => SubmitPhrase(phrase)}
																			disabled={
																				wordIndex < currentQuestionIndex - 1
																					? true
																					: false
																			}
																		>
																			{phrase}
																		</button>
																	</div>
																))}
														</div>
													</div>
												))}

											<p
												className={`text-md font-bold cursor-pointer ${themeColor?.text}`}
												onClick={() => {
													setCurrentPage(1);
												}}
											>
												{appLanguage?.seeYourSecretWordsAgain}
											</p>
										</div>
									)}
								</div>
							) : (
								//Complete Account With Name Section
								currentPage === 3 && (
									<div className="flex flex-col w-full items-center justify-between py-6 space-y-6">
										<div className="w-[50%] flex flex-col items-start justify-start space-y-4">
											<p className={`text-md font-bold ${themeColor?.text}`}>{appLanguage?.almostThere}</p>
											<p className={`text-sm text-justify ${themeColor?.text}`}>
												{appLanguage?.toMakeItEasier}
											</p>
										</div>

										<div className="flex-col relative">
											{accountImage && accountImage ? (
												<img
													className={`w-20 h-20 object-contain object-center`}
													src={accountImage}
													alt="temp"
													loading="lazy"
												/>
											) : (
												<UserIconRoundIcon className="w-20 h-20" fill={themeColor?.hex} />
											)}
											<EditIcon
												className="w-5 absolute -bottom-1 -right-1 cursor-pointer bg-white rounded-md hover:shadow-lg"
												fill={themeColor?.hex}
												onClick={() => hiddenImageFileInput.current.click()}
											/>
											<input
												style={{ display: "none" }}
												ref={hiddenImageFileInput}
												type="file"
												accept="image/x-png,image/jpeg"
												onChange={(e) => handleFileRead(e)}
											/>
										</div>

										<input
											className={`w-[50%] relative block px-3 py-2 mb-8 ${themeColor?.text} border rounded-md focus:outline-none focus:${themeColor?.ring} focus:${themeColor?.border}`}
											name="accountName"
											placeholder={appLanguage?.accountName}
											type="text"
											required
											onChange={(e) => setAccountName(e.target.value)}
										/>

										{updatingData ? (
											<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
										) : (
											<button
												className={`w-[50%] text-white ${themeColor?.bg} py-3 px-8 mt-5 rounded-md hover:shadow-lg disabled:${themeColor?.bg} disabled:bg-opacity-10 disabled:${themeColor?.text} disabled:shadow-none smooth`}
												disabled={!accountName && "true"}
												onClick={() => {
													AddAccount();
												}}
											>
												{appLanguage?.continue}
											</button>
										)}
									</div>
								)
							)}
						</>
					)}
				</div>
			</Transition>
		</div>
	);
};
export default CreateTAccount;
